import { FeatureToggles } from './featureToggles';
import { App } from './ps20apps';

/**
 * Configuration for app type mapping rules.
 * This allows different app types to be treated as equivalent or interchangeable
 * based on business logic (e.g., feature toggles, version upgrades).
 */
export interface AppTypeMappingRule {
  /** The primary app types that form an equivalence group */
  appTypes: App[];

  /** Optional: Function to determine which app type to use from the group */
  selectActiveApp?: (context?: AppSelectionContext) => App;

  /** Description of the mapping rule for documentation */
  description: string;
}

/**
 * Context provided when selecting which app to use from an equivalence group
 */
export interface AppSelectionContext {
  /** Feature toggle states */
  featureToggles?: { [key: string]: boolean };

  /** The original/source app type (e.g., from a loaded offer) */
  sourceAppType?: App;

  /** Whether this is a "beibehalten" (keep/maintain) operation */
  beibehalten?: boolean;

  /** Any additional context needed for selection */
  [key: string]: unknown;
}

export function resolveVersion(
  toggleKey: string,
  v20App: App,
  v2App: App,
  context?: AppSelectionContext,
): App {
  const featureToggleEnabled = context?.featureToggles?.[toggleKey] ?? false;
  if (!featureToggleEnabled) {
    return v20App;
  }
  const sourceAppType = context?.sourceAppType;
  const isBeibehalten = context?.beibehalten ?? false;
  if (sourceAppType === v20App) {
    return isBeibehalten ? v20App : v2App;
  }
  return v2App;
}

/**
 * Registry of all app type mapping rules.
 * Add new mapping rules here when introducing new app versions or equivalence groups.
 */
export const APP_TYPE_MAPPING_RULES: AppTypeMappingRule[] = [
  {
    appTypes: [App.TKV, App.TKV_V2],
    selectActiveApp: ctx =>
      ctx?.beibehalten && ctx?.sourceAppType === App.TKV ? App.TKV : App.TKV_V2,
    description:
      'TKV and TKV_V2 are equivalent. TKV_V2 is always active except beibehalten of a TKV source stays TKV.',
  },
  {
    appTypes: [App.VWG, App.VWG_V2],
    selectActiveApp: ctx =>
      ctx?.beibehalten && ctx?.sourceAppType === App.VWG ? App.VWG : App.VWG_V2,
    description:
      'VWG and VWG_V2 are equivalent. VWG_V2 is always active except beibehalten of a VWG source stays VWG.',
  },
  {
    appTypes: [App.VHV, App.VHV_V2],
    selectActiveApp: ctx => resolveVersion('VHV2X', App.VHV, App.VHV_V2, ctx),
    description:
      'VHV and VHV_V2 are equivalent. Toggle off => VHV. Toggle on => VHV_V2 except beibehalten of a VHV source stays VHV.',
  },
];

/**
 * Service for managing app type mappings and equivalences
 */
export class AppTypeMapper {
  private readonly mappingRules: Map<App, AppTypeMappingRule>;

  constructor(rules: AppTypeMappingRule[] = APP_TYPE_MAPPING_RULES) {
    this.mappingRules = new Map();
    rules.forEach(rule => {
      rule.appTypes.forEach(appType => {
        this.mappingRules.set(appType, rule);
      });
    });
  }

  /**
   * Check if two app types are equivalent (belong to the same mapping group)
   */
  areEquivalent(app1: App, app2: App): boolean {
    const rule1 = this.mappingRules.get(app1);
    const rule2 = this.mappingRules.get(app2);

    if (!rule1 || !rule2) {
      return app1 === app2;
    }

    return rule1 === rule2;
  }

  /**
   * Get all equivalent app types for a given app
   */
  getEquivalentApps(app: App): App[] {
    const rule = this.mappingRules.get(app);
    if (!rule) {
      return [app];
    }
    return [...rule.appTypes];
  }

  /**
   * Check if an app belongs to any mapping group
   */
  hasMappingRule(app: App): boolean {
    return this.mappingRules.has(app);
  }

  /**
   * Get the active app to use from an equivalence group
   */
  getActiveApp(app: App, context?: AppSelectionContext): App {
    const rule = this.mappingRules.get(app);
    if (!rule) {
      return app;
    }

    if (rule.selectActiveApp) {
      return rule.selectActiveApp(context);
    }

    return rule.appTypes[0];
  }

  /**
   * Check if an app is included in a list, considering equivalences
   */
  isAppInList(app: App, appList: App[]): boolean {
    return appList.some(listApp => this.areEquivalent(app, listApp));
  }

  /**
   * Filter apps to include equivalents
   */
  expandAppsWithEquivalents(apps: App[]): App[] {
    const expanded = new Set<App>();
    apps.forEach(app => {
      this.getEquivalentApps(app).forEach(equiv => expanded.add(equiv));
    });
    return Array.from(expanded);
  }

  /**
   * Normalize an app to its active version based on context
   */
  normalizeApp(app: App, context?: AppSelectionContext): App {
    const rule = this.mappingRules.get(app);
    if (!rule) {
      return app;
    }
    return this.getActiveApp(app, context);
  }
}

/**
 * Singleton instance for convenience
 */
export const appTypeMapper = new AppTypeMapper();

/**
 * Helper functions for common operations
 */

/**
 * Check if app types are equivalent
 */
export function areAppsEquivalent(app1: App, app2: App): boolean {
  return appTypeMapper.areEquivalent(app1, app2);
}

/**
 * Get all equivalent apps
 */
export function getEquivalentApps(app: App): App[] {
  return appTypeMapper.getEquivalentApps(app);
}

/**
 * Get the active app based on context (e.g., feature toggles)
 */
export function getActiveApp(app: App, context?: AppSelectionContext): App {
  return appTypeMapper.getActiveApp(app, context);
}

/**
 * Check if an app matches any in a list (considering equivalences)
 */
export function isAppInList(app: App, appList: App[]): boolean {
  return appTypeMapper.isAppInList(app, appList);
}

/**
 * Expand a list of apps to include all equivalents
 */
export function expandAppsWithEquivalents(apps: App[]): App[] {
  return appTypeMapper.expandAppsWithEquivalents(apps);
}

/**
 * Normalize app to its active version
 */
export function normalizeApp(app: App, context?: AppSelectionContext): App {
  return appTypeMapper.normalizeApp(app, context);
}


export function isTiergruppeActive(featureToggles: FeatureToggles | null | undefined): boolean {
  return featureToggles?.['TIERGRUPPE'] === true;
}

/**
 * Returns all inactive product app variants based on feature toggle state.
 * Iterates over all APP_TYPE_MAPPING_RULES and determines which app in each
 * equivalence group is inactive (i.e., not the active/selected version).
 */
export function getInactiveProductApps(featureToggles: { [key: string]: boolean }): App[] {
  const context: AppSelectionContext = { featureToggles };
  const inactiveVersions = APP_TYPE_MAPPING_RULES.flatMap(rule => {
    const activeApp = rule.selectActiveApp?.(context) ?? rule.appTypes[0];
    return rule.appTypes.filter(app => app !== activeApp);
  });
  return isTiergruppeActive(featureToggles) ? inactiveVersions : [...inactiveVersions, App.TGV];
}
